import * as React from "react";
import { cn } from "@/lib/utils";

const typographyVariants = {
  // Bold variants
  "bold-32": "font-inter font-bold text-[32px] leading-[44px] tracking-[0px]",
  "bold-26": "font-inter font-bold text-[26px] leading-[36px] tracking-[0px]",
  "bold-22": "font-inter font-bold text-[22px] leading-[30px] tracking-[0px]",
  "bold-18": "font-inter font-bold text-[18px] leading-[24px] tracking-[0px]",
  "bold-16": "font-inter font-bold text-[16px] leading-[24px] tracking-[0px]",
  "bold-14": "font-inter font-bold text-[14px] leading-[21px] tracking-[0px]",

  // SemiBold variants
  "semibold-24":
    "font-inter font-semibold text-[24px] leading-[36px] tracking-[0px]",
  "semibold-22":
    "font-inter font-semibold text-[22px] leading-[30px] tracking-[0px]",
  "semibold-18":
    "font-inter font-semibold text-[18px] leading-[30px] tracking-[0px]",
  "semibold-16":
    "font-inter font-semibold text-[16px] leading-[24px] tracking-[0px]",
  "semibold-14":
    "font-inter font-semibold text-[14px] leading-[21px] tracking-[0px]",
  "semibold-12":
    "font-inter font-semibold text-[12px] leading-[21px] tracking-[0px]",

  // Medium variants
  "medium-18":
    "font-inter font-medium text-[18px] leading-[28px] tracking-[0px]",
  "medium-16":
    "font-inter font-medium text-[16px] leading-[28px] tracking-[0px]",
  "medium-14":
    "font-inter font-medium text-[14px] leading-[20px] tracking-[0px]",
  "medium-12":
    "font-inter font-medium text-[12px] leading-[20px] tracking-[0px]",

  // Regular variants
  "regular-16":
    "font-inter text-[16px] line-height-[100%]",
  // Regular variants
  "regular-20":
    "font-inter font-normal text-[20px] leading-[32px] tracking-[0px]",
  "regular-14":
    "font-inter font-normal text-[14px] leading-[20px] tracking-[0px]",
  "regular-12":
    "font-inter font-normal text-[12px] leading-[16px] tracking-[0px]",
  "regular-10":
    "font-inter font-normal text-[10px] leading-[16px] tracking-[0px]",
} as const;

type TypographyVariant = keyof typeof typographyVariants;

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  as?: React.ElementType;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant = "medium-16", as, children, ...props }, ref) => {
    const Comp = as || getDefaultElement(variant);
    return (
      <Comp
        className={cn(typographyVariants[variant], className)}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Typography.displayName = "Typography";

function getDefaultElement(variant: TypographyVariant): React.ElementType {
  const size = variant.split("-")[1];
  const weight = variant.split("-")[0];

  // Map to semantic HTML elements based on size and weight
  if (weight === "bold") {
    if (size === "32") return "h1";
    if (size === "26") return "h2";
    if (size === "22") return "h3";
    if (size === "18") return "h4";
  }

  return "p";
}

export { Typography, typographyVariants };
